import type {
  CoPilotAgentMessageContentBlock,
  CoPilotAgentMessageDebugEntry,
} from '@terra/shared-dto-chat-types';
import { unescapeLiteralEscapeSequences } from '@terra/shared-utils';

export function normalizePayloadText(raw: string): string {
  const trimmed = raw.trim();

  if (!trimmed) {
    return '';
  }

  let resolved = trimmed;

  if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
    try {
      const parsed: unknown = JSON.parse(trimmed);

      if (Array.isArray(parsed)) {
        resolved = parsed
          .map((block) => formatContentBlock(block))
          .filter(Boolean)
          .join('\n\n');
      } else if (
        parsed &&
        typeof parsed === 'object' &&
        Object.hasOwn(parsed, 'text') &&
        typeof (parsed as { text?: unknown }).text === 'string'
      ) {
        resolved = (parsed as { text: string }).text;
      }
    } catch {
      resolved = trimmed;
    }
  }

  return unescapeLiteralEscapeSequences(resolved);
}

function formatContentBlock(block: unknown): string {
  if (typeof block === 'string') {
    return block;
  }

  if (!block || typeof block !== 'object') {
    return '';
  }

  const record = block as Record<string, unknown>;

  if (typeof record.text === 'string') {
    return record.text;
  }

  if (record.type === 'image_url') {
    return '[image block]';
  }

  if (record.cachePoint !== undefined || record.cache_control !== undefined) {
    return '[cache point]';
  }

  return JSON.stringify(block, null, 2);
}

export function extractReadableText(
  content: CoPilotAgentMessageDebugEntry['content'],
): string {
  if (typeof content === 'string') {
    return normalizePayloadText(content);
  }

  if (!Array.isArray(content) || content.length === 0) {
    return '';
  }

  return content
    .map((block: CoPilotAgentMessageContentBlock) => {
      if (typeof block.text === 'string') {
        return block.text;
      }

      if (block.type === 'image_url') {
        return '[image block]';
      }

      if (block.type === 'tool_use') {
        return `[tool_use: ${typeof block.name === 'string' ? block.name : 'unknown'}]`;
      }

      return `[${block.type ?? 'block'}]`;
    })
    .filter(Boolean)
    .join('\n\n');
}

export function isLikelyMarkdown(text: string): boolean {
  const sample = text.slice(0, 4000);

  return (
    /^#{1,6}\s/m.test(sample) ||
    /\*\*[^*]+\*\*/.test(sample) ||
    /^-\s/m.test(sample) ||
    /^>\s/m.test(sample) ||
    /```/.test(sample)
  );
}

export function formatToolCallArgs(args: unknown): string {
  if (args === undefined || args === null) {
    return '';
  }

  if (typeof args === 'string') {
    const trimmed = args.trim();

    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        return JSON.stringify(JSON.parse(trimmed), null, 2);
      } catch {
        return args;
      }
    }

    return args;
  }

  return JSON.stringify(args, null, 2);
}
